// import React, { useState } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { connect } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CardMedia,
  IconButton,
  CircularProgress,
  withStyles
} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import GetAppIcon from '@material-ui/icons/GetApp';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ShareIcon from '@material-ui/icons/Share';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import ListItemText from '@material-ui/core/ListItemText';
import Modal from '@material-ui/core/Modal';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import {
  TelegramShareButton,
  FacebookShareButton,
  WhatsappShareButton,
  TelegramIcon,
  FacebookIcon,
  WhatsappIcon,
  EmailShareButton,
  EmailIcon,
} from 'react-share';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { formatDate, formatDateForUrl, getCurrentLanguage } from '../../../utils/utils';
import ImageApi from '../../../utils/apiManager/constrollers/imageApi';
import { deleteImage, updateObjectCount } from '../../../redux/reducers/images/actions';
import { updateCategoryCount } from '../../../redux/reducers/categories/actions';
import { getGroups } from '../../../redux/reducers/groups/selectors';
import objectTranslation from '../../../translations/foundObjects.translation';

const styles = (theme) => ({
  root: {
    alignItems: 'center',
  },
  cardcontent: {
    padding: '0',
    margin: '0',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    }
  },
  modal: {
    position: 'absolute',
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(0, 0, 0),
  },
  progress: {
    position: 'absolute',
    top: '50%',
    right: '45%',
  },
  processing: {
    marginLeft: '45%',
  },
  statsItem: {
    alignItems: 'center',
    display: 'flex',
    margin: '11px 0 10px 0'
  },
  statsIcon: {
    marginRight: theme.spacing(1)
  },
  timeIcon: {
    width: 20,
    height: 20
  },
  iconPen: {
    fontSize: '1.1em',
    marginTop: 0,
    opacity: 0.3
  },
  inputBox: {
    padding: '4px',
    width: '4ch',
    margin: '0 0px 0 5px',
    fontFamily: 'Roboto',
    fontSize: '1.3em',
    fontWeight: 500,
    height: '30px',
    border: 0
  },
  topIcons: {
    float: 'right',
    margin: '10px 10px 5px 5px',
    paddingTop: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '5px',
    height: '40px',
    width: '92px',
    color: '#757575'
  },
  media: {
    height: 140,
    paddingTop: '56.25%', // 16:9
    position: 'relative'
  },
  textBox: {
    margin: '0 4px 0 25px'
  },
  shareButtons: {
    position: 'absolute',
    top: 0,
    right: 0,
    opacity: 0.5,
    '&:hover': {
      opacity: 1
    }
  },
  createdAt: {
    fontSize: '1rem'
  }
});

const ProductCard = (props) => {
  const [blob, setBlob] = useState(null);
  const [isImgLoad, setIsImgLoad] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [delAlert, setDelAlert] = useState(false);
  const [copied, setCopied] = useState(false);
  const [detectedObjects, setDetectedObjects] = useState([]);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const fetchImage = async () => {
    const saveBlob = blob;
    setIsImgLoad(true);
    setBlob(null);
    const imageApi = await ImageApi.getInstance();
    const imgData = await imageApi.fetchImage(props.product.id);

    if (imgData) {
      setIsImgLoad(false);
      setBlob(URL.createObjectURL(await imgData.blob()));
    } else {
      setIsImgLoad(false);
      setBlob(saveBlob);
    }
  };

  const fetchOriginalImage = async () => {
    setIsImgLoad(true);
    const imageApi = await ImageApi.getInstance();
    const imgData = await imageApi.fetchOriginalImage(props.product.id);

    if (imgData) {
      setIsImgLoad(false);
      setBlob(URL.createObjectURL(await imgData.blob()));
    } else {
      setIsImgLoad(false);
    }
  };

  useEffect(() => {
    if (props.product.isUploading) {
      setIsImgLoad(true);
    } else if (props.product.isProcessing) {
      fetchOriginalImage().then();
    } else {
      fetchImage().then();
    }
  }, [props.product.isUploading, props.product.isProcessing]);

  const handleClickDeletionAlertOpen = (e) => {
    e.stopPropagation();
    setDelAlert(true);
  };

  const handleDeletionAlertClose = () => {
    setDelAlert(false);
  };

  const handleDeleteImage = async () => {
    setDelAlert(false);
    const imageApi = await ImageApi.getInstance();
    const isImgDeleted = await imageApi.deleteImage(props.product.id);

    if (isImgDeleted) {
      const count = document.getElementById(`changeCountInput${props.product.id}`)
        ? document.getElementById(`changeCountInput${props.product.id}`).value : null;
      if (count) {
        props.updateCategoryCount('decrease', count, props.lastUsedGroupId);
      }
      props.deleteImage(props.product.id);
      if (props.onDelete) {
        props.onDelete(props.product.id);
      }
    }
  };

  const changeObjectsCount = async (count, previousCount) => {
    const imageApi = await ImageApi.getInstance();
    await imageApi.updateObject(count, props.product.id);
    props.updateObjectCount({
      id: props.product.id,
      count,
    });
    props.updateCategoryCount('manually', count, props.lastUsedGroupId, previousCount);
  };

  const handleChangeObjectsCount = async (event, id, previousCount) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await changeObjectsCount(event.target.value, previousCount);
    } else if (event.key === 'Escape') {
      document.getElementById(`changeCountInput${id}`).blur();
    }
  };

  const drawBoxes = (objects) => {
    if (!canvasRef.current || !imgRef.current || !objects.length) return;
    const img = imgRef.current;
    const canvas = canvasRef.current;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.style.width = `${img.offsetWidth}px`;
    canvas.style.height = `${img.offsetHeight}px`;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    objects.forEach(({
      pos_x: posX, pos_y: posY, size_x: sizeX, size_y: sizeY,
    }) => {
      ctx.fillStyle = 'rgba(255, 100, 0, 0.25)';
      ctx.fillRect(posX, posY, sizeX, sizeY);
      ctx.strokeStyle = 'rgba(255, 100, 0, 0.85)';
      ctx.lineWidth = 2;
      ctx.strokeRect(posX, posY, sizeX, sizeY);
    });
  };

  const handleImageLoad = () => {
    drawBoxes(detectedObjects);
  };

  useEffect(() => {
    if (!showModal) {
      setDetectedObjects([]);
      return;
    }
    ImageApi.getInstance().then((api) => {
      api.fetchDetectedObjects(props.product.id).then((data) => {
        const objects = data?.detectedObjects ?? (Array.isArray(data) ? data : []);
        setDetectedObjects(objects);
      });
    });
  }, [showModal]);

  useEffect(() => {
    drawBoxes(detectedObjects);
  }, [detectedObjects]);

  const handleCloseModal = async () => {
    setShowModal(false);
  };

  const handleOpenModal = async () => {
    setShowModal(true);
  };

  const handleCloseShareModal = async () => {
    setShowShareModal(false);
  };

  const getObjectCount = (product, classes) => {
    const productTemplate = props.templates.find((el) => {
      return el.id === product.templateId;
    });

    if (productTemplate?.name) {
      return (
        <span key={productTemplate.name}>
          <Typography
            display="inline"
            variant="h4"
          >
            {
              objectTranslation[getCurrentLanguage()]?.[productTemplate?.name]
              || productTemplate?.name
            }
          </Typography>
          {
            product.detectedObjectsSummary ? (
              <input
                id={`changeCountInput${props.product.id}`}
                type="text"
                className={classes.inputBox}
                onKeyDown={
                    (event) => handleChangeObjectsCount(
                      event,
                      props.product.id,
                      0,
                    )
                  }
                onBlur={() => {
                  document.getElementById(`changeCountInput${props.product.id}`).value = 0;
                }}
                defaultValue={0}
                />
            )
              : (
                <div className={classes.progress}>
                  <CircularProgress color="secondary" />
                </div>
              )
          }
        </span>
      );
    } else {
      return [];
    }
  };

  const handleOpenShareModal = async (e) => {
    e.stopPropagation();
    const imageApi = await ImageApi.getInstance();
    const shareData = await imageApi.getShareData(props.product.id);
    setShareToken(shareData.id);
    setShowShareModal(true);
  };

  const {
    className,
    classes,
    product,
  } = props;

  return (
    <Card
      className={clsx(classes.root, className)}
    >
      <CardContent
        className={classes.cardcontent}
      >
        <Box
          justifyContent="center"
          mb={3}
        >
          <CardMedia
            className={classes.media}
            onClick={handleOpenModal}
            image={blob}
            title="Click to Expand"
          >
            {
              isImgLoad
                ? (
                  <div className={classes.progress}>
                    <CircularProgress color="secondary" />
                  </div>
                )
                : ''
            }
            {/* <div style={display}> */}
            <div className={classes.shareButtons} style={!product.isProcessing ? { display: 'block' } : { display: 'none' }}>
              <Box className={clsx(classes.topIcons)}>
                <IconButton onClick={handleOpenShareModal} aria-label="share" size="small" title="Share">
                  <ShareIcon />
                </IconButton>
                <IconButton onClick={(e) => e.stopPropagation()} href={blob} download={`${product.id}.png`} aria-label="download" size="small" title="Download">
                  <GetAppIcon />
                </IconButton>
                <IconButton onClick={handleClickDeletionAlertOpen} aria-label="delete" size="small" title="Delete">
                  <DeleteIcon margin-left="20px" />
                </IconButton>
              </Box>
            </div>
          </CardMedia>
        </Box>
      </CardContent>
      <Box
        className={classes.processing}
        style={product.isProcessing ? { display: 'block' } : { display: 'none' }}
      >
        <AccessTimeIcon />
      </Box>
      <Box
        className={classes.textBox}
        style={!product.isProcessing ? { display: 'block' } : { display: 'none' }}
      >
        {
          product.detectedObjectsSummary?.length > 0
            ? product.detectedObjectsSummary.map((obj) => {
              return (
                <span key={obj.type}>
                  <Typography
                    display="inline"
                    variant="h4"
                  >
                    {objectTranslation[getCurrentLanguage()]?.[obj.type] || obj.type}
                  </Typography>
                  {
                    obj.count < 0
                      ? (
                        <div className={classes.progress}>
                          <CircularProgress color="secondary" />
                        </div>
                      )
                      : (
                        <input
                          id={`changeCountInput${props.product.id}`}
                          type="text"
                          className={classes.inputBox}
                          onKeyDown={
                            (event) => handleChangeObjectsCount(
                              event,
                              props.product.id,
                              obj.count,
                            )
                          }
                          onBlur={() => {
                            document.getElementById(`changeCountInput${props.product.id}`).value = obj.count;
                          }}
                          defaultValue={obj.count}
                        />
                      )
                  }
                </span>
              );
            }) : getObjectCount(product, classes)
        }
        <Box
          className={classes.statsItem}
          justify="space-between"
          spacing={2}
          style={product.createdAt ? { display: 'block' } : { display: 'none' }}
        >
          <Typography
            display="inline"
            variant="body2"
            className={classes.createdAt}
          >
            {product.createdAt ? formatDate(new Date(product.createdAt)) : ''}
          </Typography>
        </Box>
      </Box>
      <Dialog
        open={delAlert}
        onClose={handleDeletionAlertClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Are you sure that you want to delete this picture?</DialogTitle>
        <DialogActions>
          <Button onClick={handleDeletionAlertClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteImage} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Modal
        open={showModal}
        onClick={handleCloseModal}
        onClose={handleCloseModal}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <div
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          className={classes.modal}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              ref={imgRef}
              alt='Original'
              src={blob}
              onLoad={handleImageLoad}
              style={{ display: 'block' }}
            />
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>
      </Modal>
      <Dialog onClose={handleCloseShareModal} aria-labelledby="simple-dialog-title" open={showShareModal}>
        <DialogTitle id="simple-dialog-title">Select application</DialogTitle>
        <List>
          <ListItem>
            <WhatsappShareButton url={`${window['appUrl']}/share/${shareToken}/${formatDateForUrl(new Date())}.png`}>
              <ListItem button>
                <ListItemAvatar>
                  <Avatar className={classes.avatar}>
                    <WhatsappIcon size={32} round/>
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary="Whatsapp"/>
              </ListItem>
            </WhatsappShareButton>
          </ListItem>
          <ListItem>
            <FacebookShareButton url={`${window['appUrl']}/share/${shareToken}/${formatDateForUrl(new Date())}.png`}>
              <ListItem button>
                <ListItemAvatar>
                  <Avatar className={classes.avatar}>
                    <FacebookIcon size={32} round/>
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary="Facebook"/>
              </ListItem>
            </FacebookShareButton>
          </ListItem>
          <ListItem>
            <TelegramShareButton url={`${window['appUrl']}/share/${shareToken}/${formatDateForUrl(new Date())}.png`}>
              <ListItem button>
                <ListItemAvatar>
                  <Avatar className={classes.avatar}>
                    <TelegramIcon size={32} round/>
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary="Telegram"/>
              </ListItem>
            </TelegramShareButton>
          </ListItem>
          <ListItem>
            <EmailShareButton url={`${window['appUrl']}/share/${shareToken}/${formatDateForUrl(new Date())}.png`}>
              <ListItem button>
                <ListItemAvatar>
                  <Avatar className={classes.avatar}>
                    <EmailIcon size={32} round/>
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary="Email"/>
              </ListItem>
            </EmailShareButton>
          </ListItem>
          <ListItem>
            <CopyToClipboard
              text={`${window['appUrl']}/share/${shareToken}/${formatDateForUrl(new Date())}.png`}
              onCopy={() => setCopied(true)}
            >
              <ListItem button>
                <ListItemAvatar>
                  <Avatar className={classes.avatar}>
                    <FileCopyIcon size={32} round/>
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary="Copy URL to the clipboard"/>
              </ListItem>
            </CopyToClipboard>
          </ListItem>
        </List>
      </Dialog>
    </Card>
  );
};

ProductCard.propTypes = {
  index: PropTypes.number,
  className: PropTypes.string,
  product: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  templates: PropTypes.array,
  deleteImage: PropTypes.func,
  updateCategoryCount: PropTypes.func,
};

export default connect(
  getGroups,
  {
    deleteImage,
    updateCategoryCount,
    updateObjectCount,
  }
)(withStyles(styles)(ProductCard));
